<script lang="ts" setup>
import { computed, inject, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import BigNumber from '@exchange/helpers/bignumber';
import usePromise from '@exchange/libs/composables/shared/src/lib/usePromise';
import { modalService } from '@exchange/libs/modals/src';
import { subaccountsService, SubaccountType, type Subaccount } from '@exchange/libs/trading-accounts/service/src';
import { getSubaccountCreateInfo } from '@exchange/libs/trading-accounts/ui/src/lib/components/subaccount-create-flow';
import { currencyService, type CurrencyModel } from '@exchange/libs/utils/currency/src';

const props = defineProps({
  isFuturesMarket: { type: Boolean, required: true },
});

const { defaultFiatCurrency, currencies, fiatPrecision } = currencyService;
const { fetchList: fetchSubaccounts } = subaccountsService;

// Add fetch promise handler
const { createPromise: subaccountsFetchNow } = usePromise(fetchSubaccounts);

const emit = defineEmits<{
  (e: 'selectSubaccount', subaccountId: string): void;
  (e: 'cancelModal'): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const balanceMasked = inject('balanceMasked', ref(false));

// Match AccountListBoard's implementation exactly
const getAccountTotalBalanceInFiat = (account: Subaccount, currenciesList: Dictionary<CurrencyModel> | undefined) => {
  if (!currenciesList) return new BigNumber(0);
  return account.getBalanceTotal(currenciesList).all;
};

const subaccountsList = computed(() => {
  if (!currencies.value) return [];

  return subaccountsService.list.value
    .filter((account) => (props.isFuturesMarket ? account.type === SubaccountType.FUTURES : account.type === SubaccountType.SPOT))
    .sort((a, b) => {
      const balanceA = getAccountTotalBalanceInFiat(a, currencies.value);
      const balanceB = getAccountTotalBalanceInFiat(b, currencies.value);
      return new BigNumber(balanceB).minus(balanceA).toNumber();
    });
});

// Fetch data when modal is mounted
onMounted(() => {
  subaccountsFetchNow();
});

const onAccountClick = async (accountId: string) => {
  await subaccountsService.switchTo(accountId);
  emit('selectSubaccount', accountId);
};

const getAccountTypeLabel = (type: SubaccountType): string => {
  switch (type) {
    case SubaccountType.STANDARD:
      return 'Spot';
    case SubaccountType.INSTANT_TRADE:
      return 'Swap';
    case SubaccountType.SPOT:
      return 'Spot';
    case SubaccountType.FUTURES:
      return 'Futures';
    default:
      return type;
  }
};

// Add ref for scroll container
const scrollContainer = ref<HTMLElement | null>(null);
const showBottomShadow = ref(true);

// Add computed property to check if scrolling is possible
const shouldShowShadow = computed(() => subaccountsList.value.length > 5);

// Update scroll handler to consider both conditions
const handleScroll = (event: Event) => {
  if (!shouldShowShadow.value) {
    showBottomShadow.value = false;
    return;
  }

  const target = event.target as HTMLElement;
  const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
  showBottomShadow.value = !isAtBottom;
};

const { subaccountCreate, subaccountCreateLoading } = getSubaccountCreateInfo();

const router = useRouter();

const onCreateClick = async () =>
  subaccountCreate(
    {
      showSuccessModal: true,
      switchToNewAccount: true,
      defaultAccountType: props.isFuturesMarket ? SubaccountType.FUTURES : SubaccountType.SPOT,
    },
    router,
  );
</script>

<template>
  <x-modal
    class="subaccount-select-modal"
    :title="t('Select account')"
    variant="dark"
    @modal-close="emit('cancelModal')"
  >
    <template #content>
      <div class="subaccount-select-modal__content">
        <div
          ref="scrollContainer"
          class="subaccount-select-modal__scroll-container"
          @scroll="handleScroll"
        >
          <template v-if="currencies && subaccountsList.length">
            <template
              v-for="(account, idx) in subaccountsList"
              :key="account.accountId"
            >
              <x-list-item
                class="subaccount-select-modal__item"
                :class="{ 'subaccount-select-modal__item--main': idx === 0 }"
                :title="account.name"
                @click="onAccountClick(account.accountId)"
              >
                <template #subtitle>
                  <ot-balance
                    :value="getAccountTotalBalanceInFiat(account, currencies)"
                    :precision="fiatPrecision"
                    :symbol="defaultFiatCurrency"
                    :masked="balanceMasked"
                  />
                </template>
                <template #label-additional-content>
                  <ot-lozenge
                    :text="getAccountTypeLabel(account.type)"
                    class="subaccount-select-modal__type-lozenge"
                    :filled="false"
                  />
                </template>
              </x-list-item>
            </template>
          </template>
          <template v-else-if="currencies && !subaccountsList.length">
            <div class="subaccount-select-modal__empty-state">
              You don't have any {{ props.isFuturesMarket ? 'futures' : 'spot' }} sub accounts. Create one by clicking on the button below
            </div>
          </template>
          <template v-else>
            <ot-spinner class="subaccount-select-modal__spinner" />
          </template>
        </div>
        <div
          v-if="shouldShowShadow && showBottomShadow"
          class="subaccount-select-modal__shadow subaccount-select-modal__shadow--bottom"
        />
      </div>
    </template>
    <template #buttons>
      <x-button
        class="subaccount-select-modal__cancel"
        variant="primary"
        :block="true"
        :text="t('Create subaccount')"
        :loading="subaccountCreateLoading"
        @click="onCreateClick"
      />
    </template>
  </x-modal>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.subaccount-select-modal {
  &__content {
    position: relative;
  }

  &__scroll-container {
    max-height: 400px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgb(var(--v-theme-elevation-3)) transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgb(var(--v-theme-elevation-3));
      border-radius: 3px;
    }
  }

  &__shadow {
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 1;
    width: 100%;
    height: 20px;
    pointer-events: none;
    background: linear-gradient(to top, rgb(var(--v-theme-surface)) 0%, rgba(var(--v-theme-surface), 0) 100%);
    transition: opacity 0.2s ease;
  }

  &__item {
    --account-list-item-active-color: rgb(var(--v-theme-secondary));

    position: relative;
    overflow: hidden;
    height: 59px;
    padding: 12px 16px;
    border: 1px solid rgb(var(--v-theme-elevation-2));
    border-radius: var(--app-border-radius);
    margin: 8px 0;
    cursor: pointer;
    transition: background-color 0.3s;

    &--main {
      --account-list-item-active-color: rgb(var(--v-theme-primary));
    }

    &:hover {
      background-color: rgb(var(--v-theme-elevation-1));
    }
  }

  &__empty-state {
    padding: 24px 16px;
    text-align: center;
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-sm);
  }

  &__type {
    width: max-content;
    margin: 8px 0;
  }

  &__type-lozenge {
    margin: 0 0 0 auto;
    font-size: var(--font-size-xs);
  }

  &__main-account-tag-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
